import './App.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Navibar from './components/layout/Navibar';
import NavibarCollasped from './components/layout/NavibarCollasped';
import Sidebar from './components/layout/Sidebar';
import RouteContextBar from './components/layout/RouteContextBar';
import AppRouteFallback from './components/ui/AppRouteFallback';
import ModuleErrorBoundary from './components/ui/ModuleErrorBoundary';
import AccountAccessNotice from './components/ui/AccountAccessNotice';
import {
  buildRoleAccessRefreshRequest,
  buildUserAccessRefreshRequest,
  normalizeAccountDetails,
  selectRoleRecord,
} from './appAccessInitialization';
import AccountSetup from './components/pages/company/Company'
import UserSetup from './components/pages/user/User'
import Dashboard from './components/pages/dashboard/Dashboard'
import AccountLocations from './components/pages/companyLocation/CompanyLocation'
import Policies from './components/pages/policies/Policies'
import LocationSetup from './components/pages/locationSetup/LocationSetup'
import BusinessHours from './components/pages/businessHours/BusinessHours'
import TaxSetup from './components/pages/taxSetup/TaxSetup'
import ExtrasSetup from './components/pages/extrasSetup/ExtrasSetup'
import OneWayRentalSetup from './components/pages/oneWayRentalSetup/OneWayRentalSetup'
import FleetDescription from "./components/pages/fleetDescription/FleetDescription"
import AccountRateCodeSetup from "./components/pages/accountRateCodeSetup/AccountRateCodeSetup"
import AccountRateRules from "./components/pages/accountRateRules/AccountRateRules"
import ReservationSourceSetup from "./components/pages/reservationSourceSetup/ReservationSourceSetup"
import RateCodeSetup from "./components/pages/rateCodeSetup/RateCodeSetup"
import MinimumRate from './components/pages/minimumRate/MinimumRate';
import RateUpdate from './components/pages/rateUpdate/RateUpdate';
import Adjustments from "./components/pages/adjustments/Adjustments"
import Promotions from "./components/pages/promotions/Promotions"
import Blackouts from "./components/pages/blackouts/Blackouts"
import UtilizationBasedAdjustment from "./components/pages/utilizationBasedAdjustment/UtilizationBasedAdjustment"
import UpdateInventory from "./components/pages/updateInventory/UpdateInventory";
import Login from "./components/pages/login/Login";
import RolesSetup from "./components/pages/rolesSetup/RolesSetup";
import ErrorCodes from "./components/pages/errorCodes/ErrorCodes";
import Cars from "./components/pages/cars/Cars";
import Reports from "./components/pages/reports/Reports";
import CapacityControl from "./components/pages/capacityControl/CapacityControl";
// import Europcars from "./components/pages/europcars/Europcars";
import Reservations from './components/pages/reservations/Reservations';
import NoShow from './components/pages/noShow/NoShow';
import FetchConsole from './components/pages/fetchConsole/FetchConsole';
import RateID from './components/pages/rateID/RateID';
import EventLog from './components/pages/eventLog/EventLog';
import OptionalServicesStandard from './components/pages/optionalServicesStandard/OptionalServicesStandard';
import FleetStandard from './components/pages/fleetStandard/FleetStandard';
import RateSnapshot from './components/pages/rateSnapshot/RateSnapshot';
import OptionalServicesImages from './components/pages/optionalServicesImages/OptionalServicesImages';
import MyProfile from './components/pages/my_profile/MyProfile';
import AffiliateOTAMapping from './components/pages/affiliateOTAMapping/AffiliateOTAMapping';
import BankReconciliationReport from './components/pages/bankReconciliationReport/BankReconciliationReport';
import CommissionScreen from './components/pages/commissionScreen/commissionScreen';
import CompetitorSetup from './components/pages/competitorSetup/CompetitorSetup';
import MinimumRateMapping from './components/pages/minimumRateMapping/MinimumRateMapping';
import EnableAutomation from './components/pages/enableAutomation/EnableAutomation';
import AutomationScheduler from './components/pages/automationSetUp/AutomationSetUp';
import CurrencyConverter from './components/pages/currencyConverter/CurrencyConverter';
import SupportScreen from './components/pages/supportScreen/SupportScreen';
import ViewBaseRates from './components/pages/viewBaseRates/ViewBaseRates';
import ReservationInsights from "./components/pages/ReservationInsights/ReservationInsights";
import CeilingRate from "./components/pages/ceilingRate/CeilingRate";
import PolicyExpedia from "./components/pages/policyExpedia/PolicyExpedia";
// import DBRCleanUp from "./components/pages/dBRCleanUp/DBRCleanUp";
import InstantAutomationPro from './components/pages/instantAutomationPro/InstantAutomationPro'
import Competitors from './components/pages/competitors/Competitors'
import ReviewScreen from './components/pages/reviewScreen/ReviewScreen'
import AutomationReport from './components/pages/automationReport/AutomationReport';
import PartnerReport from './components/pages/partnerReport/PartnerReport';
import MarkUpInfo from './components/pages/markupInfo/MarkUpInfo';


import { baseURL } from './components/pages/backend_url';
import axios from 'axios';

const INACTIVITY_TIMEOUT_MS = 10 * 60 * 1000;
const LAST_ACTIVITY_STORAGE_KEY = 'mvp_last_activity_at';
const LOGOUT_REASON_STORAGE_KEY = 'mvp_logout_reason';
const DRAWER_NAVIGATION_QUERY = '(max-width: 991.98px)';
const COMPACT_NAVIGATION_QUERY = '(max-width: 1199.98px)';

const readStoredUser = () => {
  try {
    const storedUser = sessionStorage.getItem('user_token');
    const parsedUser = storedUser ? JSON.parse(storedUser) : null;

    return parsedUser && typeof parsedUser === 'object' ? parsedUser : null;
  } catch (error) {
    return null;
  }
};

const normalizeRecords = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  return payload && typeof payload === 'object' ? [payload] : [];
};

const normalizeModuleName = (moduleName) => (
  typeof moduleName === 'string' ? moduleName.trim().replace(/\s+/g, '') : ''
);

const hasModuleAccess = (roleDefinition, moduleName) => {
  const accessSpecifier = roleDefinition[`${moduleName}_Access Specifier`];
  return accessSpecifier === 'Edit' || accessSpecifier === 'View';
};

const selectCurrentUserRecord = (records, accountId) => {
  const normalizedAccountId = accountId === undefined || accountId === null
    ? ''
    : String(accountId).trim();
  const validRecords = records.filter((record) => record && typeof record === 'object');
  const accountMatch = validRecords.find(
    (record) => String(record.company_id ?? record.account_id ?? '').trim() === normalizedAccountId
  );

  return accountMatch || (
    !normalizedAccountId && validRecords.length === 1 ? validRecords[0] : null
  );
};

const logoutForInactivity = () => {
  sessionStorage.clear();
  sessionStorage.setItem(LOGOUT_REASON_STORAGE_KEY, 'inactivity');
  window.location.replace('/?session=inactive');
};

function App() {

  const [collaspedFlag, setCollapsedFlag] = useState(
    () => typeof window !== 'undefined'
      && window.matchMedia(COMPACT_NAVIGATION_QUERY).matches
  );
  const [drawerNavigation, setDrawerNavigation] = useState(
    () => typeof window !== 'undefined'
      && window.matchMedia(DRAWER_NAVIGATION_QUERY).matches
  );
  const [sidebarColor, setSidebarColor] = useState("");
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [accessInitialization, setAccessInitialization] = useState(
    () => readStoredUser() ? 'loading' : 'idle'
  );
  const [moduleDetail, setModuleDetail] = useState([]);
  const [moduleDetailHash, setModuleDetailHash] = useState({});

  useEffect(() => {
    const drawerMedia = window.matchMedia(DRAWER_NAVIGATION_QUERY);
    const compactMedia = window.matchMedia(COMPACT_NAVIGATION_QUERY);

    const syncNavigationWithViewport = () => {
      const nextDrawerNavigation = drawerMedia.matches;

      setDrawerNavigation(nextDrawerNavigation);
      setCollapsedFlag(nextDrawerNavigation || compactMedia.matches);
    };

    drawerMedia.addEventListener('change', syncNavigationWithViewport);
    compactMedia.addEventListener('change', syncNavigationWithViewport);

    return () => {
      drawerMedia.removeEventListener('change', syncNavigationWithViewport);
      compactMedia.removeEventListener('change', syncNavigationWithViewport);
    };
  }, []);

  useEffect(() => {
    const mobileDrawerOpen = loginSuccess && drawerNavigation && !collaspedFlag;
    document.body.classList.toggle('mvp-mobile-nav-open', mobileDrawerOpen);

    return () => {
      document.body.classList.remove('mvp-mobile-nav-open');
    };
  }, [collaspedFlag, drawerNavigation, loginSuccess]);


  useEffect(() => {
    let cancelled = false;

    const initializeAuthenticatedApp = async () => {
      const foundUser = readStoredUser();

      if (!foundUser) {
        setAccessInitialization('idle');
        return;
      }

      const storedActivity = Number(sessionStorage.getItem(LAST_ACTIVITY_STORAGE_KEY));
      if (
        Number.isFinite(storedActivity)
        && storedActivity > 0
        && Date.now() - storedActivity >= INACTIVITY_TIMEOUT_MS
      ) {
        logoutForInactivity();
        return;
      }

      setAccessInitialization('loading');

      try {
        const sessionAccountId = String(foundUser.account_id ?? '').trim();
        if (!sessionAccountId) {
          throw new Error('CURRENT_USER_ACCESS_INCOMPLETE');
        }

        const res = await axios.get(baseURL + "/get_modules");
        if (cancelled) {
          return;
        }

        const moduleRecords = normalizeRecords(res.data)
          .filter((record) => normalizeModuleName(record?.name));
        const modulesWithProfile = foundUser.role === 'Users'
          ? [
              ...moduleRecords,
              ...(
                moduleRecords.some((record) => normalizeModuleName(record.name) === 'MyProfile')
                  ? []
                  : [{ name: 'MyProfile', favourite: {} }]
              ),
            ]
          : moduleRecords;
        let temp_hash = {};

        const temp_module_arr = modulesWithProfile
          .filter((val) => (
            normalizeModuleName(val.name) !== 'MultipleAccountsAdjustment&Promotion'
          ))
          .map((val) => {

          const temp_mod_name = normalizeModuleName(val.name);

          if (temp_mod_name === 'Account')
            temp_hash[temp_mod_name] = <AccountSetup />

          else if (temp_mod_name === 'User')
            temp_hash[temp_mod_name] = <UserSetup />

          else if (temp_mod_name === 'AccountLocations')
            temp_hash[temp_mod_name] = <AccountLocations />

          else if (temp_mod_name === 'Policies')
            temp_hash[temp_mod_name] = <Policies />

          else if (temp_mod_name === 'Location')
            temp_hash[temp_mod_name] = <LocationSetup />

          else if (temp_mod_name === 'BusinessHours')
            temp_hash[temp_mod_name] = <BusinessHours />

          else if (temp_mod_name === 'Tax')
            temp_hash[temp_mod_name] = <TaxSetup />

          else if (temp_mod_name === 'OptionalServices')
            temp_hash[temp_mod_name] = <ExtrasSetup />

          else if (temp_mod_name === 'OneWayRental')
            temp_hash[temp_mod_name] = <OneWayRentalSetup />

          else if (temp_mod_name === 'FleetDescription')
            temp_hash[temp_mod_name] = <FleetDescription />

          else if (temp_mod_name === 'AccountRateCode')
            temp_hash[temp_mod_name] = <AccountRateCodeSetup />

          else if (temp_mod_name === 'AccountRateRules')
            temp_hash[temp_mod_name] = <AccountRateRules />

          else if (temp_mod_name === 'ReservationSource')
            temp_hash[temp_mod_name] = <ReservationSourceSetup />

          else if (temp_mod_name === 'RateCode')
            temp_hash[temp_mod_name] = <RateCodeSetup />

          else if (temp_mod_name === 'MinimumRate')
            temp_hash[temp_mod_name] = <MinimumRate />

          else if (temp_mod_name === 'RateUpdate')
            temp_hash[temp_mod_name] = <RateUpdate />

          else if (temp_mod_name === 'Adjustment')
            temp_hash[temp_mod_name] = <Adjustments />

          else if (temp_mod_name === 'Promotion')
            temp_hash[temp_mod_name] = <Promotions />

          else if (temp_mod_name === 'Blackout')
            temp_hash[temp_mod_name] = <Blackouts />

          else if (temp_mod_name === 'UtilizationBasedAdjustment')
            temp_hash[temp_mod_name] = <UtilizationBasedAdjustment />

          else if (temp_mod_name === 'UpdateInventory')
            temp_hash[temp_mod_name] = <UpdateInventory />

          else if (temp_mod_name === 'AccessSetup')
            temp_hash[temp_mod_name] = <RolesSetup />

          else if (temp_mod_name === 'ErrorCodes')
            temp_hash[temp_mod_name] = <ErrorCodes />

          else if (temp_mod_name === 'Cars')
            temp_hash[temp_mod_name] = <Cars />

          else if (temp_mod_name === 'Reports')
            temp_hash[temp_mod_name] = <Reports />

          else if (temp_mod_name === 'CapacityControl')
            temp_hash[temp_mod_name] = <CapacityControl />

          // else if (temp_mod_name === 'Europcars')
          //   temp_hash[temp_mod_name] = <Europcars />

          else if (temp_mod_name === 'Reservations')
            temp_hash[temp_mod_name] = <Reservations />

          else if (temp_mod_name === 'NoShow')
            temp_hash[temp_mod_name] = <NoShow />

          else if (temp_mod_name === 'FetchConsole')
            temp_hash[temp_mod_name] = <FetchConsole />

          else if (temp_mod_name === 'RateID')
            temp_hash[temp_mod_name] = <RateID />


          else if (temp_mod_name === 'EventLog')
            temp_hash[temp_mod_name] = <EventLog />

          else if (temp_mod_name === 'OptionalServicesStandard')
            temp_hash[temp_mod_name] = <OptionalServicesStandard />

          else if (temp_mod_name === 'FleetStandard')
            temp_hash[temp_mod_name] = <FleetStandard />

          else if (temp_mod_name === 'RateSnapshot')
            temp_hash[temp_mod_name] = <RateSnapshot />

          else if (temp_mod_name === 'OptionalServicesImages')
            temp_hash[temp_mod_name] = <OptionalServicesImages />

          else if (temp_mod_name === 'MyProfile')
            temp_hash[temp_mod_name] = <MyProfile />

          else if (temp_mod_name === 'AffiliateOTAMapping')
            temp_hash[temp_mod_name] = <AffiliateOTAMapping />

          else if (temp_mod_name === 'BankReconciliationReport')
            temp_hash[temp_mod_name] = <BankReconciliationReport />

          else if (temp_mod_name === 'CommissionScreen')
            temp_hash[temp_mod_name] = <CommissionScreen />
          
            else if (temp_mod_name === 'CompetitorSetup')
            temp_hash[temp_mod_name] = <CompetitorSetup />

          else if (temp_mod_name === 'MinimumRateMapping')
            temp_hash[temp_mod_name] = <MinimumRateMapping />

          else if (temp_mod_name === 'EnableAutomation')
            temp_hash[temp_mod_name] = <EnableAutomation />

          else if (temp_mod_name === 'AutomationScheduler')
            temp_hash[temp_mod_name] = <AutomationScheduler />

          else if (temp_mod_name === 'CurrencyConverter')
          temp_hash[temp_mod_name] = <CurrencyConverter />

          else if (temp_mod_name === 'SupportScreen')
            temp_hash[temp_mod_name] = <SupportScreen />
          
          else if (temp_mod_name === 'ViewBaseRates')
          temp_hash[temp_mod_name] = <ViewBaseRates />
          
          else if (temp_mod_name === "ReservationInsights")
            temp_hash[temp_mod_name] = <ReservationInsights />;

          else if (temp_mod_name === "CeilingRate")
            temp_hash[temp_mod_name] = <CeilingRate />;
          
          else if (temp_mod_name === "PolicyExpedia")
            temp_hash[temp_mod_name] = <PolicyExpedia />;
            
          // else if (temp_mod_name === "DBRCleanUp")
          //   temp_hash[temp_mod_name] = <DBRCleanUp />;

          else if (temp_mod_name === 'InstantAutomationPro')
            temp_hash[temp_mod_name] = <InstantAutomationPro />;
            
          else if (temp_mod_name === 'Competitors')
            temp_hash[temp_mod_name] = <Competitors />;
          
          else if (temp_mod_name === 'ReviewScreen')
            temp_hash[temp_mod_name] = <ReviewScreen />;
          
          else if (temp_mod_name === 'AutomationReport')
            temp_hash[temp_mod_name] = <AutomationReport />;
          
          else if (temp_mod_name === 'PartnerReport')
            temp_hash[temp_mod_name] = <PartnerReport />;
          
          else if (temp_mod_name === 'MarkUpInfo')
            temp_hash[temp_mod_name] = <MarkUpInfo />;

          return temp_mod_name;
        })
          .filter(Boolean);
        const availableModules = temp_module_arr.filter((moduleName) => temp_hash[moduleName]);

        const res1 = await axios.post(
          baseURL + "/get_individual_user_details",
          buildUserAccessRefreshRequest(foundUser.userEmail, sessionAccountId)
        );
          const currentUser = selectCurrentUserRecord(
            normalizeRecords(res1.data),
            foundUser.account_id
          );

          if (!currentUser) {
            throw new Error('CURRENT_USER_RECORD_UNAVAILABLE');
          }

          const rawSelectedRole = String(currentUser.selected_role ?? '');
          const selectedRole = rawSelectedRole.trim();
          const currentAccountId = currentUser.company_id
            ?? currentUser.account_id
            ?? foundUser.account_id;

          if (!selectedRole || currentAccountId === undefined || currentAccountId === null) {
            throw new Error('CURRENT_USER_ACCESS_INCOMPLETE');
          }

          const res2 = await axios.post(
            baseURL + "/get_user_role_detail",
            buildRoleAccessRefreshRequest(rawSelectedRole, currentAccountId)
          );
          const roleRecord = selectRoleRecord(
            normalizeRecords(res2.data),
            selectedRole,
            currentAccountId
          );

          if (!roleRecord) {
            throw new Error('ROLE_DEFINITION_UNAVAILABLE');
          }

          const roleCapabilities = {
            all_modules_access: roleRecord.all_modules_access === true,
            account_scope: roleRecord.account_scope || 'Permission based',
          };
          const roleDefinition = {
            ...roleRecord.role_definition,
            'MyProfile_Access Specifier': 'Edit',
          };

          if (roleCapabilities.all_modules_access) {
            availableModules.forEach((moduleName) => {
              roleDefinition[`${moduleName}_Access Specifier`] = 'Edit';
              roleDefinition[`${moduleName}_Account Access`] = roleCapabilities.account_scope;
              [
                'Rate Code Selection',
                'Copy Rates',
                'Days Applicable Selection',
                'Excel Upload',
                'OTA Selection',
                'Car Class Selection',
              ].forEach((permissionName) => {
                roleDefinition[`${moduleName}_${permissionName}`] = 'Yes';
              });
              roleDefinition[`${moduleName}_Type of Adjustment/ Promotion / Blackout`] = 'All';
            });
          }
          const currentAccountType = normalizeAccountDetails(
            currentUser.user_account_type,
            foundUser.account_type,
            currentAccountId
          );

          if (!currentAccountType) {
            throw new Error('ACCOUNT_DETAILS_UNAVAILABLE');
          }

          const synchronizedUser = {
            ...foundUser,
            userName: currentUser.user_name || foundUser.userName,
            userEmail: currentUser.user_email || foundUser.userEmail,
            account_id: currentAccountId,
            account_type: currentAccountType,
            selected_role: selectedRole,
            access: roleDefinition,
            role_capabilities: roleCapabilities,
            account_allocated: currentUser.account_allocated || [String(currentAccountId)],
          };

          sessionStorage.setItem('user_token', JSON.stringify(synchronizedUser));

          const tempMenuItems = roleCapabilities.all_modules_access
            ? availableModules
            : availableModules.filter((moduleName) => (
              moduleName === 'SupportScreen'
              || (
                moduleName === 'ViewBaseRates'
                && hasModuleAccess(roleDefinition, 'RateUpdate')
              )
              || hasModuleAccess(roleDefinition, moduleName)
            ));

          if (cancelled) {
            return;
          }

          setModuleDetailHash(temp_hash);
          setModuleDetail(tempMenuItems);

        setLoginSuccess(true);
        setAccessInitialization('ready');
      } catch (error) {
        if (cancelled) {
          return;
        }

        console.error(
          'Unable to initialize authenticated workspace:',
          error?.message || 'Unknown error'
        );
        setLoginSuccess(false);
        setModuleDetailHash({});
        setModuleDetail([]);
        setAccessInitialization('error');
      }
    };

    initializeAuthenticatedApp();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!loginSuccess) {
      return undefined;
    }

    let inactivityTimer;
    let lastRecordedAt = 0;

    const scheduleInactivityCheck = () => {
      window.clearTimeout(inactivityTimer);

      const storedActivity = Number(sessionStorage.getItem(LAST_ACTIVITY_STORAGE_KEY));
      const lastActivity = Number.isFinite(storedActivity) && storedActivity > 0
        ? storedActivity
        : Date.now();

      if (lastActivity !== storedActivity) {
        sessionStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, lastActivity.toString());
      }

      const remainingTime = INACTIVITY_TIMEOUT_MS - (Date.now() - lastActivity);

      if (remainingTime <= 0) {
        logoutForInactivity();
        return;
      }

      inactivityTimer = window.setTimeout(logoutForInactivity, remainingTime);
    };

    const recordActivity = () => {
      const currentTime = Date.now();

      if (currentTime - lastRecordedAt < 1000) {
        return;
      }

      lastRecordedAt = currentTime;
      sessionStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, currentTime.toString());
      scheduleInactivityCheck();
    };

    const checkAfterVisibilityChange = () => {
      if (!document.hidden) {
        scheduleInactivityCheck();
      }
    };

    const activityEvents = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart'];
    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, recordActivity, { passive: true });
    });
    window.addEventListener('focus', scheduleInactivityCheck);
    document.addEventListener('visibilitychange', checkAfterVisibilityChange);

    scheduleInactivityCheck();

    return () => {
      window.clearTimeout(inactivityTimer);
      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, recordActivity);
      });
      window.removeEventListener('focus', scheduleInactivityCheck);
      document.removeEventListener('visibilitychange', checkAfterVisibilityChange);
    };
  }, [loginSuccess]);


  return (
    <>
      <Router>

        {loginSuccess && (
          <a className="mvpSkipLink" href="#mvp-main-content">
            Skip to main content
          </a>
        )}

        {!loginSuccess ? "" : (<>
          {collaspedFlag
            ? <NavibarCollasped
                setLoginSuccess={setLoginSuccess}
                drawerNavigation={drawerNavigation}
                onNavigationToggle={() => setCollapsedFlag(false)}
              />
            : <Navibar
                setSidebarColor={setSidebarColor}
                sidebarColor={sidebarColor}
                setLoginSuccess={setLoginSuccess}
                drawerNavigation={drawerNavigation}
                onNavigationToggle={() => setCollapsedFlag(true)}
              />}

          {(!drawerNavigation || !collaspedFlag) && (
            <div className="customSidebar">
              <Sidebar
                collapsed={drawerNavigation ? false : collaspedFlag}
                setCollapsedFlag={setCollapsedFlag}
                sidebarColor={sidebarColor}
                drawerNavigation={drawerNavigation}
              />
            </div>
          )}

          {drawerNavigation && !collaspedFlag && (
            <button
              type="button"
              className="mobileSidebarBackdrop"
              aria-label="Close navigation"
              onClick={() => setCollapsedFlag(true)}
            />
          )}
        </>)}
        <div
          id="mvp-main-content"
          tabIndex={-1}
          className={!loginSuccess
            ? ""
            : `appMainContent ${collaspedFlag ? 'rightSideContentCollasped' : 'rightSideContent'}`}
        >

          {loginSuccess && <RouteContextBar />}
          {loginSuccess && <AccountAccessNotice />}

          <Routes>
            {!loginSuccess && accessInitialization === 'loading' ? (
              <Route
                path="*"
                element={<AppRouteFallback mode="loading" standalone />}
              />
            ) : !loginSuccess && accessInitialization === 'error' ? (
              <Route
                path="*"
                element={(
                  <AppRouteFallback
                    mode="setupError"
                    standalone
                    onRetry={() => window.location.reload()}
                    onSignOut={() => {
                      sessionStorage.clear();
                      window.location.replace('/');
                    }}
                  />
                )}
              />
            ) : !loginSuccess ? (
              <Route path="*" element={<Login setLoginSuccess={setLoginSuccess} />} />
            ) : (
              <>
                <Route
                  exact
                  path="/"
                  element={(
                    <ModuleErrorBoundary resetKey="Dashboard">
                      <Dashboard />
                    </ModuleErrorBoundary>
                  )}
                />
                {
                  moduleDetail.length > 0 ? moduleDetail.map((val) => (
                    <Route
                      key={val}
                      exact
                      path={"/" + val}
                      element={(
                        <ModuleErrorBoundary resetKey={val}>
                          {moduleDetailHash[val]}
                        </ModuleErrorBoundary>
                      )}
                    />
                  )) : ""
                }
                <Route path="*" element={<AppRouteFallback mode="unavailable" />} />
              </>
            )}
          </Routes>
        </div>


      </Router>
    </>
  );
}

export default App;
